/**
 * 관리자 파일 업로더 (단일/다중, 드래그앤드롭).
 * - data-props 외부 주입: uploadUrl, category, multiple?, accept? (allowed-types), maxCount?, maxSizeBytes?, maxSizeMB?
 * - 최대 개수(maxCount), 허용 파일 타입(accept), 최대 용량(maxSizeBytes 또는 maxSizeMB) 설정 가능.
 */

import { Logger } from "../../utils/Logger";
import { Modal } from "../../utils/Modal";

export type FileUploaderCategory = "image" | "video" | "audio" | "document" | "archive";

export interface FileUploaderProps {
  uploadUrl: string;
  category: FileUploaderCategory;
  targetType?: string;
  targetId?: number;
  multiple?: boolean;
  /** 허용 파일 타입 (HTML accept 또는 "allowed-types") */
  accept?: string;
  /** 최대 업로드 개수 (다중 업로드 시) */
  maxCount?: number;
  /** 파일 1개당 최대 용량 (bytes) */
  maxSizeBytes?: number;
  /** 파일 1개당 최대 용량 (MB). maxSizeBytes 없을 때 사용 */
  maxSizeMB?: number;
  /** attachment meta_json.role (예: community_slider). 업로드 시 서버로 전달 */
  metaRole?: string;
  chunkInitUrl?: string;
  chunkChunkUrl?: string;
  chunkCompleteUrl?: string;
  /** 이 크기(bytes) 초과 시 청크 업로드 (video + chunk URL 있을 때) */
  chunkThresholdBytes?: number;
}


const DEFAULT_ACCEPT: Record<FileUploaderCategory, string> = {
  image: "image/*",
  video: "video/*",
  audio: "audio/*",
  document: ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.hwp",
  archive: ".zip,.rar,.7z,.tar,.gz",
};

const CATEGORY_LABEL: Record<FileUploaderCategory, string> = {
  image: "이미지",
  video: "영상",
  audio: "오디오",
  document: "문서",
  archive: "압축파일",
};

/** Font Awesome 7 Pro – 타입별 드롭존 아이콘 */
const CATEGORY_ICON: Record<FileUploaderCategory, string> = {
  image: "fa-solid fa-image",
  video: "fa-solid fa-film",
  audio: "fa-solid fa-music",
  document: "fa-solid fa-file-lines",
  archive: "fa-solid fa-file-zipper",
};

function getCsrfToken(): string {
  const input = document.querySelector<HTMLInputElement>('input[name="_token"]');
  return input?.value ?? "";
}

function parseDataProps(el: HTMLElement): FileUploaderProps | null {
  const raw = el.getAttribute("data-props");
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const uploadUrl = typeof parsed.uploadUrl === "string" ? parsed.uploadUrl : "";
    const category = (parsed.category as FileUploaderCategory) ?? "image";
    if (!uploadUrl) return null;
    const accept = typeof parsed.accept === "string" ? parsed.accept
      : typeof (parsed as Record<string, unknown>)["allowed-types"] === "string"
        ? (parsed as Record<string, unknown>)["allowed-types"] as string
        : undefined;
    const maxCount = typeof parsed.maxCount === "number" && parsed.maxCount > 0 ? parsed.maxCount : undefined;
    let maxSizeBytes: number | undefined;
    if (typeof parsed.maxSizeBytes === "number" && parsed.maxSizeBytes > 0) {
      maxSizeBytes = parsed.maxSizeBytes;
    } else if (typeof parsed.maxSizeMB === "number" && parsed.maxSizeMB > 0) {
      maxSizeBytes = Math.floor(parsed.maxSizeMB * 1024 * 1024);
    }
    return {
      uploadUrl,
      category: ["image", "video", "audio", "document", "archive"].includes(category)
        ? category
        : "image",
      targetType: typeof parsed.targetType === "string" ? parsed.targetType : undefined,
      targetId: typeof parsed.targetId === "number" ? parsed.targetId : undefined,
      multiple: Boolean(parsed.multiple),
      accept,
      maxCount,
      maxSizeBytes,
      metaRole: typeof parsed.metaRole === "string" ? parsed.metaRole : undefined,
      chunkInitUrl: typeof parsed.chunkInitUrl === "string" ? parsed.chunkInitUrl : undefined,
      chunkChunkUrl: typeof parsed.chunkChunkUrl === "string" ? parsed.chunkChunkUrl : undefined,
      chunkCompleteUrl: typeof parsed.chunkCompleteUrl === "string" ? parsed.chunkCompleteUrl : undefined,
      chunkThresholdBytes:
        typeof parsed.chunkThresholdBytes === "number" && parsed.chunkThresholdBytes > 0
          ? parsed.chunkThresholdBytes
          : undefined,
    };
  } catch {
    return null;
  }
}

function parseDataInitial(el: HTMLElement): UploadedItem[] {
  const raw = el.getAttribute("data-initial");
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw) as unknown[];
    if (!Array.isArray(arr)) return [];
    return arr
      .map((x) => {
        const o = x as Record<string, unknown>;
        const id = typeof o.id === "number" ? o.id : 0;
        const file_url = typeof o.file_url === "string" ? o.file_url : "";
        const file_type = typeof o.file_type === "string" ? o.file_type : "image";
        if (!id || !file_url) return null;
        return {
          id,
          file_url,
          file_type,
          original_name: typeof o.original_name === "string" ? o.original_name : undefined,
          size_bytes: typeof o.size_bytes === "number" ? o.size_bytes : undefined,
        };
      })
      .filter((x): x is UploadedItem => x != null);
  } catch {
    return [];
  }
}

export interface UploadedItem {
  id: number;
  file_url: string;
  file_type: string;
  original_name?: string;
  size_bytes?: number;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

export class FileUploaderComponent {
  private readonly root: HTMLElement;
  private readonly props: FileUploaderProps;
  private readonly input: HTMLInputElement;
  private readonly dropZone: HTMLElement;
  private readonly listEl: HTMLElement;
  private uploaded: UploadedItem[] = [];
  private dragCounter = 0;

  constructor(root: HTMLElement, props: FileUploaderProps) {
    this.root = root;
    this.props = props;
    this.input = this.root.querySelector<HTMLInputElement>('input[type="file"]') ?? this.createInput();
    this.dropZone = this.createDropZone();
    this.listEl = this.root.querySelector<HTMLElement>("[data-uploader-list]") ?? this.createListEl();
    this.uploaded = parseDataInitial(root);
  }

  private createInput(): HTMLInputElement {
    const input = document.createElement("input");
    input.type = "file";
    input.className = "admin-file-uploader__input";
    input.accept = this.props.accept ?? DEFAULT_ACCEPT[this.props.category];
    input.multiple = this.props.multiple ?? false;
    input.style.position = "absolute";
    input.style.opacity = "0";
    input.style.width = "0";
    input.style.height = "0";
    input.style.pointerEvents = "none";
    return input;
  }

  private getMaxSizeBytes(): number | undefined {
    return this.props.maxSizeBytes;
  }

  private createDropZone(): HTMLElement {
    const zone = document.createElement("div");
    zone.className = "admin-file-uploader__dropzone";
    zone.setAttribute("data-uploader-dropzone", "");
    const iconClass = CATEGORY_ICON[this.props.category];
    const label = CATEGORY_LABEL[this.props.category] + " 업로드";
    const multi = this.props.multiple ? " (여러 파일)" : "";
    const maxCount = this.props.maxCount;
    const maxSize = this.getMaxSizeBytes();
    const parts: string[] = ["파일을 끌어오거나 클릭하여 선택"];
    if (maxCount != null) parts.push(`최대 ${maxCount}개`);
    if (maxSize != null) parts.push(`파일당 ${formatSize(maxSize)} 이하`);
    const hint = parts.join(" · ");
    zone.innerHTML = `
      <span class="admin-file-uploader__dropzone-icon" aria-hidden="true"><i class="${iconClass}"></i></span>
      <span class="admin-file-uploader__dropzone-text">${label}${multi}</span>
      <span class="admin-file-uploader__dropzone-hint">${hint}</span>
    `;
    return zone;
  }

  private createListEl(): HTMLElement {
    const div = document.createElement("div");
    div.setAttribute("data-uploader-list", "");
    div.className = "admin-file-uploader__list";
    if (!this.root.querySelector("[data-uploader-list]")) {
      this.root.appendChild(div);
    }
    return div;
  }

  init(): void {
    this.input.accept = this.props.accept ?? DEFAULT_ACCEPT[this.props.category];
    this.input.multiple = this.props.multiple ?? false;

    const choiceEl = this.root.querySelector<HTMLElement>(".admin-single-image-uploader__choice, .admin-single-video-uploader__choice, .admin-single-audio-uploader__choice");
    if (choiceEl) {
      choiceEl.appendChild(this.dropZone);
    } else {
      if (!this.input.parentElement) {
        this.root.insertBefore(this.input, this.listEl);
      }
      if (!this.dropZone.parentElement) {
        this.root.insertBefore(this.dropZone, this.listEl);
      }
    }
    this.dropZone.appendChild(this.input);

    this.dropZone.addEventListener("click", () => {
      if (this.isAtMaxCount()) return;
      this.input.click();
    });
    this.input.addEventListener("change", () => this.handleFiles(this.input.files));

    this.dropZone.addEventListener("dragover", (e) => this.handleDragOver(e));
    this.dropZone.addEventListener("dragleave", () => this.handleDragLeave());
    this.dropZone.addEventListener("drop", (e) => this.handleDrop(e));

    this.renderList();
    this.updateDropZoneState();
    this.root.classList.add("admin-file-uploader-box--inited");
    const fallback = this.root.querySelector<HTMLElement>(".admin-file-uploader__fallback");
    if (fallback) fallback.setAttribute("aria-hidden", "true");
    const fallbackInput = this.root.querySelector<HTMLInputElement>("[data-uploader-fallback]");
    if (fallbackInput) fallbackInput.removeAttribute("name");
  }

  private isAtMaxCount(): boolean {
    const max = this.props.maxCount;
    return max != null && this.uploaded.length >= max;
  }

  private updateDropZoneState(): void {
    const atMax = this.isAtMaxCount();
    this.dropZone.classList.toggle("admin-file-uploader__dropzone--disabled", atMax);
    this.dropZone.querySelector(".admin-file-uploader__dropzone-hint")?.replaceWith(
      (() => {
        const span = document.createElement("span");
        span.className = "admin-file-uploader__dropzone-hint";
        const maxCount = this.props.maxCount;
        const maxSize = this.getMaxSizeBytes();
        const parts: string[] = [atMax ? "최대 개수 도달" : "파일을 끌어오거나 클릭하여 선택"];
        if (maxCount != null && !atMax) parts.push(`최대 ${maxCount}개`);
        if (maxSize != null) parts.push(`파일당 ${formatSize(maxSize)} 이하`);
        span.textContent = parts.join(" · ");
        return span;
      })()
    );
  }

  private handleDragOver(e: DragEvent): void {
    e.preventDefault();
    e.stopPropagation();
    this.dragCounter++;
    this.dropZone.classList.add("admin-file-uploader__dropzone--dragover");
  }

  private handleDragLeave(): void {
    this.dragCounter--;
    if (this.dragCounter <= 0) {
      this.dragCounter = 0;
      this.dropZone.classList.remove("admin-file-uploader__dropzone--dragover");
    }
  }

  private handleDrop(e: DragEvent): void {
    e.preventDefault();
    e.stopPropagation();
    this.dragCounter = 0;
    this.dropZone.classList.remove("admin-file-uploader__dropzone--dragover");
    const files = e.dataTransfer?.files;
    if (files?.length) this.handleFiles(files);
  }

  private showUploadLoader(): void {
    this.root.classList.add("admin-file-uploader-box--loading");
    const loader = document.createElement("div");
    loader.className = "admin-file-uploader__dropzone-loader";
    loader.setAttribute("aria-hidden", "true");
    loader.innerHTML = '<i class="fa-solid fa-spinner fa-spin" aria-hidden="true"></i><span>업로드 중...</span>';
    this.dropZone.appendChild(loader);
  }

  private hideUploadLoader(): void {
    this.root.classList.remove("admin-file-uploader-box--loading");
    const loader = this.dropZone.querySelector(".admin-file-uploader__dropzone-loader");
    if (loader) loader.remove();
  }

  private async handleFiles(fileList: FileList | null): Promise<void> {
    if (!fileList?.length) return;
    let files = Array.from(fileList);
    if (!this.props.multiple && files.length > 1) {
      files = files.slice(0, 1);
    }
    const maxSizeBytes = this.getMaxSizeBytes();
    const maxCount = this.props.maxCount;
    if (maxCount != null) {
      const remaining = Math.max(0, maxCount - this.uploaded.length);
      if (remaining <= 0) {
        void Modal.alert(`최대 ${maxCount}개까지 업로드할 수 있습니다.`);
        this.input.value = "";
        return;
      }
      if (files.length > remaining) {
        files = files.slice(0, remaining);
        if (files.length < fileList.length) {
          void Modal.alert(`최대 ${maxCount}개까지 가능하여 ${remaining}개만 추가합니다.`);
        }
      }
    }
    try {
      this.showUploadLoader();
      for (const file of files) {
        if (maxSizeBytes != null && file.size > maxSizeBytes) {
          void Modal.alert(`"${file.name}"은(는) 허용 용량(${formatSize(maxSizeBytes)})을 초과합니다.`);
          continue;
        }
        await this.uploadOne(file);
      }
      this.renderList();
    } finally {
      this.hideUploadLoader();
    }
    this.input.value = "";
  }

  private parseUploadPayload(json: Record<string, unknown>, file: File): void {
    const ok = (json?.ok ?? json?.success) === true || json?.result === "success";
    const data = (json?.data ?? json) as Record<string, unknown> | undefined;
    if (!ok) {
      const msg = (json?.msg ?? json?.message ?? "업로드에 실패했습니다.") as string;
      void Modal.alert(msg);
      return;
    }
    const id = typeof data?.id === "number" ? data.id : 0;
    const file_url = typeof data?.file_url === "string" ? data.file_url : "";
    const file_type = typeof data?.file_type === "string" ? data.file_type : this.props.category;
    const original_name = typeof data?.original_name === "string" ? data.original_name : file.name;
    const size_bytes = typeof data?.size_bytes === "number" ? data.size_bytes : file.size;
    if (id && file_url) {
      this.uploaded.push({ id, file_url, file_type, original_name, size_bytes });
    }
  }

  private async uploadVideoChunked(file: File): Promise<void> {
    const initUrl = this.props.chunkInitUrl ?? "";
    const chunkUrl = this.props.chunkChunkUrl ?? "";
    const completeUrl = this.props.chunkCompleteUrl ?? "";
    if (!initUrl || !chunkUrl || !completeUrl) {
      await this.uploadOneDirect(file);
      return;
    }
    const csrf = getCsrfToken();
    try {
      const fdInit = new FormData();
      fdInit.append("expected_size", String(file.size));
      fdInit.append("original_name", file.name);
      if (csrf) fdInit.append("_token", csrf);
      const rInit = await fetch(initUrl, {
        method: "POST",
        body: fdInit,
        headers: { "X-Requested-With": "XMLHttpRequest", Accept: "application/json" },
      });
      const jInit = (await rInit.json()) as Record<string, unknown>;
      const dInit = (jInit?.data ?? jInit) as Record<string, unknown> | undefined;
      const token = typeof dInit?.token === "string" ? dInit.token : "";
      const maxChunk =
        typeof dInit?.max_chunk_bytes === "number" && dInit.max_chunk_bytes > 0
          ? dInit.max_chunk_bytes
          : 5 * 1024 * 1024;
      if (!token) {
        void Modal.alert((jInit?.msg as string) || "청크 초기화에 실패했습니다.");
        return;
      }
      let offset = 0;
      let index = 0;
      while (offset < file.size) {
        const end = Math.min(offset + maxChunk, file.size);
        const blob = file.slice(offset, end);
        const fd = new FormData();
        fd.append("token", token);
        fd.append("chunk_index", String(index));
        fd.append("file", blob, file.name);
        if (csrf) fd.append("_token", csrf);
        const rCh = await fetch(chunkUrl, {
          method: "POST",
          body: fd,
          headers: { "X-Requested-With": "XMLHttpRequest", Accept: "application/json" },
        });
        const jCh = (await rCh.json()) as Record<string, unknown>;
        const okCh = (jCh?.ok ?? jCh?.success) === true || jCh?.result === "success";
        if (!okCh) {
          void Modal.alert((jCh?.msg as string) || "청크 업로드에 실패했습니다.");
          return;
        }
        offset = end;
        index += 1;
      }
      const fdDone = new FormData();
      fdDone.append("token", token);
      fdDone.append("category", this.props.category);
      if (this.props.targetType) fdDone.append("target_type", this.props.targetType);
      if (this.props.targetId != null) fdDone.append("target_id", String(this.props.targetId));
      if (this.props.metaRole) fdDone.append("meta_role", this.props.metaRole);
      if (csrf) fdDone.append("_token", csrf);
      const rDone = await fetch(completeUrl, {
        method: "POST",
        body: fdDone,
        headers: { "X-Requested-With": "XMLHttpRequest", Accept: "application/json" },
      });
      const jDone = (await rDone.json()) as Record<string, unknown>;
      this.parseUploadPayload(jDone, file);
    } catch (e) {
      Logger.error("FileUploader chunked upload failed", e);
      void Modal.alert("업로드 중 오류가 발생했습니다.");
    }
  }

  private async uploadOneDirect(file: File): Promise<void> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("category", this.props.category);
    if (this.props.targetType) formData.append("target_type", this.props.targetType);
    if (this.props.targetId != null) formData.append("target_id", String(this.props.targetId));
    if (this.props.metaRole) formData.append("meta_role", this.props.metaRole);
    const token = getCsrfToken();
    if (token) formData.append("_token", token);

    try {
      const res = await fetch(this.props.uploadUrl, {
        method: "POST",
        body: formData,
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json",
        },
      });
      const json = (await res.json()) as Record<string, unknown>;
      this.parseUploadPayload(json, file);
    } catch (e) {
      Logger.error("FileUploader upload failed", e);
      void Modal.alert("업로드 중 오류가 발생했습니다.");
    }
  }

  private async uploadOne(file: File): Promise<void> {
    const hasChunk =
      this.props.category === "video" &&
      this.props.chunkInitUrl &&
      this.props.chunkChunkUrl &&
      this.props.chunkCompleteUrl;
    const threshold = this.props.chunkThresholdBytes ?? 8 * 1024 * 1024;
    if (hasChunk && file.size > threshold) {
      await this.uploadVideoChunked(file);
      return;
    }
    await this.uploadOneDirect(file);
  }

  private renderList(): void {
    this.listEl.innerHTML = "";
    this.root.classList.toggle("admin-file-uploader-box--has-item", this.uploaded.length > 0);
    if (this.uploaded.length > 0) {
      const title = document.createElement("p");
      title.className = "admin-input-label";
      title.textContent = "업로드된 파일";
      this.listEl.appendChild(title);

      const grid = document.createElement("div");
      grid.className = "admin-file-uploader__grid admin-file-uploader__grid--" + this.props.category;

      this.uploaded.forEach((item, index) => {
        const card = this.renderItem(item, index);
        grid.appendChild(card);
      });
      this.listEl.appendChild(grid);
    }
    this.updateDropZoneState();
  }

  private renderItem(item: UploadedItem, index: number): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "admin-file-uploader__item";

    if (this.props.category === "image" && (item.file_type === "image" || /\.(jpe?g|png|gif|webp|bmp)/i.test(item.original_name ?? item.file_url))) {
      const img = document.createElement("img");
      img.src = item.file_url;
      img.alt = item.original_name ?? "";
      img.className = "admin-file-uploader__thumb";
      img.loading = "lazy";
      wrap.appendChild(img);
    } else if (this.props.category === "video" && (item.file_type === "video" || item.file_url.match(/\.(mp4|webm|ogg)/i))) {
      const video = document.createElement("video");
      video.src = item.file_url;
      video.controls = true;
      video.className = "admin-file-uploader__preview-video";
      video.preload = "metadata";
      wrap.appendChild(video);
    } else if (this.props.category === "audio" && (item.file_type === "audio" || item.file_url.match(/\.(mp3|wav|ogg|m4a)/i))) {
      const audio = document.createElement("audio");
      audio.src = item.file_url;
      audio.controls = true;
      audio.className = "admin-file-uploader__preview-audio";
      wrap.appendChild(audio);
    } else {
      const icon = document.createElement("span");
      icon.className = "admin-file-uploader__icon";
      icon.setAttribute("aria-hidden", "true");
      if (this.props.category === "document") {
        icon.innerHTML = "<i class=\"fa-solid fa-file-lines\"></i>";
      } else {
        icon.innerHTML = "<i class=\"fa-solid fa-file-zipper\"></i>";
      }
      wrap.appendChild(icon);
    }

    const meta = document.createElement("div");
    meta.className = "admin-file-uploader__meta";
    const nameEl = document.createElement("div");
    nameEl.className = "admin-file-uploader__name";
    nameEl.title = item.original_name ?? "";
    nameEl.textContent = item.original_name ?? "파일";
    meta.appendChild(nameEl);
    if (item.size_bytes != null) {
      const sizeEl = document.createElement("div");
      sizeEl.className = "admin-file-uploader__size";
      sizeEl.textContent = formatSize(item.size_bytes);
      meta.appendChild(sizeEl);
    }
    wrap.appendChild(meta);

    const actions = document.createElement("div");
    actions.className = "admin-file-uploader__actions";
    const link = document.createElement("a");
    link.href = item.file_url;
    link.target = "_blank";
    link.rel = "noopener";
    link.textContent = "보기";
    link.className = "admin-btn admin-btn--sm";
    const removeBtn = document.createElement("button");
    removeBtn.type = "button";
    removeBtn.className = "admin-btn admin-btn--danger admin-btn--sm";
    removeBtn.textContent = "제거";
    removeBtn.addEventListener("click", () => {
      this.uploaded.splice(index, 1);
      this.renderList();
    });
    actions.appendChild(link);
    actions.appendChild(removeBtn);
    wrap.appendChild(actions);

    const hidden = document.createElement("input");
    hidden.type = "hidden";
    hidden.name = this.root.getAttribute("data-attachment-name") ?? "attachment_ids[]";
    hidden.value = String(item.id);
    wrap.appendChild(hidden);

    return wrap;
  }

  getUploaded(): UploadedItem[] {
    return [...this.uploaded];
  }

  /** 업로드 목록 비우기 (파트너 추가 후 썸네일 초기화 등) */
  reset(): void {
    this.uploaded = [];
    this.renderList();
    this.updateDropZoneState();
  }

  /** 수정 시 기존 이미지 URL을 업로더에 표시 (id 0 = 기존 썸네일 유지) */
  setExistingPreview(fileUrl: string): void {
    const url = fileUrl.trim();
    if (!url) {
      this.reset();
      return;
    }
    this.uploaded = [
      { id: 0, file_url: url, file_type: "image" },
    ];
    this.renderList();
    this.updateDropZoneState();
  }
}

const SELECTOR = "[data-file-uploader]";

declare global {
  interface HTMLElement {
    _fileUploaderComponent?: FileUploaderComponent;
  }
}

export function initFileUploaders(): void {
  const nodes = document.querySelectorAll<HTMLElement>(SELECTOR);
  nodes.forEach((el) => {
    const props = parseDataProps(el);
    if (!props) {
      Logger.warn("FileUploader: missing or invalid data-props", el);
      return;
    }
    const component = new FileUploaderComponent(el, props);
    component.init();
    el._fileUploaderComponent = component;
  });
}
